import { useState } from 'react'
import { ChevronsUpDown, Check, Plus, SlidersHorizontal, Trash2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { Workspace } from '@/data/mock'
import { useWorkspace } from '@/contexts/WorkspaceContext'

interface WorkspaceSwitcherProps {
  workspaces: Workspace[]
  currentWorkspaceId: string
  onWorkspaceChange: (id: string) => void
  onCreateWorkspace?: (name: string) => void
  onOpenWorkspaceSettings?: () => void
}

export function WorkspaceSwitcher({
  workspaces,
  currentWorkspaceId,
  onWorkspaceChange,
  onCreateWorkspace,
  onOpenWorkspaceSettings,
}: WorkspaceSwitcherProps) {
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const { deleteWorkspace } = useWorkspace()

  const handleDelete = async (ws: Workspace) => {
    if (!window.confirm(`Delete workspace "${ws.name}"? This cannot be undone.`)) return
    try {
      await deleteWorkspace(ws.id)
    } catch (err) {
      if (err instanceof Error && err.message === 'WORKSPACE_HAS_SOURCES') {
        alert('Remove all PDFs from this workspace before deleting it.')
      } else {
        alert('Failed to delete workspace.')
      }
    }
  }

  const current = workspaces.find((ws) => ws.id === currentWorkspaceId)

  const handleCreate = () => {
    const trimmed = newName.trim()
    if (!trimmed) return
    onCreateWorkspace?.(trimmed)
    setNewName('')
    setCreateOpen(false)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center justify-between w-full rounded-md px-2 py-1.5 text-sm font-semibold text-foreground hover:bg-accent transition-colors focus:outline-none">
            <span className="truncate">{current?.name ?? 'Select workspace'}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 ml-1" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          {workspaces.map((ws) => (
            <DropdownMenuItem
              key={ws.id}
              onClick={() => onWorkspaceChange(ws.id)}
              className="flex items-center justify-between group"
            >
              <span className="truncate">{ws.name}</span>
              <span className="flex items-center gap-1 shrink-0 ml-1">
                {ws.id === currentWorkspaceId && (
                  <Check className="h-3.5 w-3.5 text-pharma-primary" />
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(ws) }}
                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                  title="Delete workspace"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onOpenWorkspaceSettings?.()}>
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Workspace Settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Create Workspace</DialogTitle>
          </DialogHeader>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Workspace name"
            className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate() }}
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={!newName.trim()}
              className="bg-pharma-primary hover:bg-pharma-primary/90 text-white"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
